<template>
  <div>
    <h3 v-if="isFirstLegalRepresentative && hasSlovakiaNationality"
        class="heading-3 weight-medium pb-10 pb-md-16 text-left text-md-center">
      Enter the Birth Number of the Authorised Person
    </h3>
    <h3 v-if="isFirstLegalRepresentative && !hasSlovakiaNationality"
        class="heading-3 weight-medium pb-10 pb-md-16 text-left text-md-center">
      Enter the Birth Number or BIČ of the Authorised Person
    </h3>

    <DoubleInlineRadioButton
      v-if="!hasSlovakiaNationality"
      class="mb-10 weight-regular body-3"
      first-title='Yes'
      second-title='No'
      @input="setHasBirthNumber"
      :value="legalRepresentative.hasBirthNumber"
      :name="'birthNumber'"
      v-validate="errors"
    >
      <h4 v-if="isFirstLegalRepresentative" class="weight-medium text-left mb-10">
        Does the authorised person have a birth number?
      </h4>
      <h4 v-else class="weight-medium text-left mb-10">
        Does the other authorised person have a birth number?
      </h4>
    </DoubleInlineRadioButton>

    <div class="input-box-holder" v-if="legalRepresentative.hasBirthNumber">
      <div v-validate="errors" v-has-value="legalRepresentative.birthNumber">
        <TextField
          class="input-box"
          title='Birth Number'
          description='Enter without slash (this will be added automatically)'
          :value="legalRepresentative.birthNumber"
          :id="`birth-number_${personIndex}`"
          v-mask="'######/####'"
          @input="setBirthNumber"
          required
        />
        <div class="validation-output text-left mt-1">
          <ErrorHandler class="error" :id="`V305_${personIndex}`">
            Enter the birth number.
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V306_${personIndex}`">
            Enter the birth number in the correct format.
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V307_${personIndex}`">
            The legal representative must be over 18 years of age.
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V222_${personIndex}`">
            Enter the birth number.
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V298_${personIndex}`">
            Enter the birth number.
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V192_${personIndex}`">
            Enter the birth number in the correct format.
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V299_${personIndex}`">
            Enter the birth number in the correct format.
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V223_${personIndex}`">
            The legal representative must be over 18 years of age.
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V300_${personIndex}`">
            The legal representative must be over 18 years of age.
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V251_${personIndex}`">
            This birth number has already been used.
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V302_${personIndex}`">
            This birth number has already been used.
          </ErrorHandler>
        </div>
      </div>
    </div>

    <div v-if="!legalRepresentative.hasBirthNumber && !hasSlovakiaNationality">
      <div class="input-box-holder flex flex-col gap-2 color-gray-500 my-5" v-validate="errors" v-has-value="legalRepresentative.birthDate">
        <DateField
          class="input-box"
          v-model="legalRepresentative.birthDate"
          blockFutureDate
        >
          <p class="text-left weight-medium pb-5 m-0">
            Date of Birth
          </p>
        </DateField>
        <div class="validation-output text-left">
          <ErrorHandler class="error" :id="`V321_${personIndex}`">
            Enter date of birth.
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V322_${personIndex}`">
            Enter date of birth in the correct format.
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V323_${personIndex}`">
            Enter date of birth in the correct format.
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V288_${personIndex}`">
            Enter date of birth.
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V289_${personIndex}`">
            Enter date of birth in the correct format.
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V323_${personIndex}`">
            Enter date of birth in the correct format.
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V223_${personIndex}`">
            The legal representative must be over 18 years of age.
          </ErrorHandler>
        </div>
      </div>
    </div>

    <div v-if="!legalRepresentative.hasBirthNumber && !hasSlovakiaNationality">
      <div
        class="input-box-holder"
        v-validate="errors"
        v-has-value="legalRepresentative.birthNumber"
      >
        <TextField
          class="input-box"
          title='BIČ'
          :id="`bic_${personIndex}`"
          v-mask="'##########'"
          v-model.trim="legalRepresentative.birthNumber"
          required
        >
          <Tooltip slot="tooltip">
            <p class="text-left m-0">
              
    <b>BIČ</b> or Meaningless Identification Number is a 10-digit alphanumeric code
    that replaces the birth number if you are a foreign national, residing in the territory of
    the Slovak Republic and participating in the public health insurance scheme.
    <br/>
    <br/>
    If you have been assigned a BIČ in the territory of the Slovak Republic in the past, please provide it.
    If not, one will be assigned to you by the UDZS when evaluating the acceptance of your application.
  
            </p>
          </Tooltip>
        </TextField>
        <div class="validation-output text-left mt-2">
          <ErrorHandler class="error" :id="`V325_${personIndex}`">
            Enter BIČ
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V324_${personIndex}`">
            Enter BIČ in the correct format, e.g. XX7XXXYYYYY.
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V293_${personIndex}`">
            Enter BIČ
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V291_${personIndex}`">
            Enter BIČ in the correct format, e.g. XX7XXXYYYYY.
          </ErrorHandler>
        </div>
      </div>

      <div
        class="input-box-holder"
        v-validate="errors"
        v-has-value="legalRepresentative.sex"
      >
        <DoubleInlineRadioButton
          class="mb-10 weight-regular body-3"
          first-title='Male'
          second-title='Female'
          :value="initLegalRepresentativeSex"
          @input="setLegalRepresentativeSex"
          :name="'sex'"
          v-validate="errors"
        >
          <h4 class="weight-medium text-left my-10">
            Sex
          </h4>
        </DoubleInlineRadioButton>
        <div class="validation-output text-left mt-3">
          <ErrorHandler class="error" :id="`V294_${personIndex}`">
            Select one of the options.
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V295_${personIndex}`">
            Select one of the options.
          </ErrorHandler>

          <ErrorHandler class="error" :id="`V303_${personIndex}`">
            Select one of the options.
          </ErrorHandler>
        </div>
      </div>
    </div>

    <div v-if="isFirstLegalRepresentative">
      <h3 class="weight-medium text-left text-md-center mt-10 mb-15">
        Select an ID Document
      </h3>

      <div class="input-box-holder" v-validate="errors" v-has-value="legalRepresentative.evidenceType">
        <div class="validation-output text-left mb-5">
          <ErrorHandler class="error" :id="`V318_${personIndex}`">
            You have not selected any of the options. To continue, please select one option.
          </ErrorHandler>
        </div>

        <RadioButton
          v-for="(option) in evidenceTypeEnum"
          class="input-box rd-card mt-10 body-1 heading-4 weight-medium"
          :key="option.name$"
          :title="option.label"
          :value="legalRepresentative.evidenceType"
          :option="option"
          :name="'evidenceTypeGroup'"
          :checked="isSelected(option)"
          @input="setEvidenceType"
        >
          <IdentityVerificationRadioContent
            :person="legalRepresentative"
            :errors="errors"
            :index="personIndex"
          />
        </RadioButton>
      </div>
    </div>
  </div>
</template>

<script>
import DoubleInlineRadioButton from '@/components/DoubleInlineRadioButton/DoubleInlineRadioButton.vue';
import RadioButton from '@/components/RadioButton/RadioButton.vue';
import TextField from '@/components/TextField/TextField.vue';
import { COUNTRY_CODE_FOR_SLOVAKIA } from '@/config';
import enums from '@/mixins/enums';
import persons from '@/mixins/persons';
import IdentityVerificationRadioContent from '@/pagesComponents/IdentityVerificationRadioContent.vue';
import ErrorHandler from '@/components/ErrorHandler/ErrorHandler.vue';
import DateField from '@/components/DateField/DateField.vue';
import { areCodesValid } from '@/utils/validations';
import Tooltip from '@/components/Tooltip/Tooltip.vue';

export default {
  components: {
    Tooltip,
    DateField,
    TextField,
    RadioButton,
    DoubleInlineRadioButton,
    IdentityVerificationRadioContent,
    ErrorHandler,
  },
  mixins: [enums, persons],
  props: {
    errors: { type: Array, required: true },
    model: { type: Object, required: true },
    legalRepresentative: { type: Object, required: true },
    personIndex: { type: Number, required: true },
    isFirstLegalRepresentative: { type: Boolean, required: true },
  },
  methods: {
    isSelected(option) {
      return this.legalRepresentative.evidenceType === option;
    },
    setHasBirthNumber(bool) {
      this.legalRepresentative.hasBirthNumber = bool;
      this.legalRepresentative.birthNumber = '';
      this.legalRepresentative.birthDate = null;
      this.legalRepresentative.sex = null;
    },
    setBirthNumber(birthNumber) {
      this.legalRepresentative.birthNumber = birthNumber?.trim();
      this.$nextTick(() => {
        if (this.isBirthNumberValid) {
          const { getDateFromBirthNumber } = this.$kotlin.helpers.DateHelper;
          const { SexEnum } = this.enums;
          this.legalRepresentative.birthDate = getDateFromBirthNumber(birthNumber, this.model.now);
          this.legalRepresentative.sex = (+birthNumber.slice(2, 4) - 50) > 0 ? SexEnum.FEMALE : SexEnum.MALE;
        }
      });
    },
    setEvidenceType(type) {
      this.legalRepresentative.evidenceType = type;
    },
    setLegalRepresentativeSex(isMale) {
      const { SexEnum } = this.enums;
      this.legalRepresentative.sex = isMale ? SexEnum.MALE : SexEnum.FEMALE;
    },
  },
  computed: {
    hasSlovakiaNationality() { return this.legalRepresentative.nationality?.value === COUNTRY_CODE_FOR_SLOVAKIA; },
    isBirthNumberValid() {
      const birthNumberCodes = ['V273', 'V274'].map((code) => `${code}_${this.personIndex}`);
      return areCodesValid(birthNumberCodes, this.errors);
    },
    evidenceTypeEnum() {
      const { EvidenceTypeEnum: options } = this.enumsValues;
      const { EvidenceTypeEnum: enumeration } = this.enums;
      return options.filter((option) => {
        if (!this.hasSlovakiaNationality) return true;
        return option !== enumeration.RESIDENCE_PERMIT;
      });
    },
    initLegalRepresentativeSex() {
      const { SexEnum } = this.enums;
      if (this.legalRepresentative.sex) {
        return this.legalRepresentative.sex === SexEnum.MALE;
      }

      return null;
    },
  },
};
</script>
